/** 编辑器状态与操作。所有会改变作品的操作都经过 commit()，
 * 保证撤销/重做覆盖新增、移动、缩放、删除、清空、尺寸、折法与设置（规格书 5.2）。
 */
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import type { CutShape, FoldId, Project, Vec } from '../domain/types';
import { createDefaultProject, migrateSizeForFold, validateProject, serializeProject } from '../domain/project/schema';
import { buildPresetCuts, type Preset } from '../content/presets';

export type Tool = 'select' | 'polygon' | 'freehand' | 'circle' | 'triangle' | 'rect';

export interface Notice {
  kind: 'info' | 'warn' | 'error' | 'success';
  text: string;
  at: number;
}

export type ViewMode = 'folded' | 'unfolded';

export interface EditorState {
  project: Project;
  tool: Tool;
  selectedId: string | null;
  draft: Vec[] | null;
  past: Project[];
  future: Project[];
  notice: Notice | null;
  mode: ViewMode;
  saving: boolean;
}

export type Action =
  | { type: 'setTool'; tool: Tool }
  | { type: 'setMode'; mode: ViewMode }
  | { type: 'select'; id: string | null }
  | { type: 'setDraft'; points: Vec[] | null }
  | { type: 'addCut'; cut: CutShape }
  | { type: 'replaceCut'; cut: CutShape }
  | { type: 'removeCut'; id: string }
  | { type: 'clearCuts' }
  | { type: 'setFold'; foldId: FoldId; keepCuts: boolean }
  | { type: 'setSize'; value: number[] }
  | { type: 'setColor'; color: string }
  | { type: 'setGuides'; patch: Partial<Project['guides']> }
  | { type: 'loadProject'; project: Project }
  | { type: 'loadPreset'; preset: Preset }
  | { type: 'undo' }
  | { type: 'redo' }
  | { type: 'notice'; notice: Notice | null }
  | { type: 'saving'; saving: boolean };

const MAX_HISTORY = 80;

function withHistory(state: EditorState, project: Project): EditorState {
  return {
    ...state,
    project,
    past: [...state.past, state.project].slice(-MAX_HISTORY),
    future: [],
  };
}

export function reducer(state: EditorState, action: Action): EditorState {
  switch (action.type) {
    case 'setTool':
      return { ...state, tool: action.tool, draft: action.tool === 'polygon' || action.tool === 'freehand' ? state.draft : null };
    case 'setMode':
      return { ...state, mode: action.mode };
    case 'select':
      return { ...state, selectedId: action.id };
    case 'setDraft':
      return { ...state, draft: action.points };
    case 'addCut':
      return {
        ...withHistory(state, { ...state.project, cuts: [...state.project.cuts, action.cut] }),
        selectedId: action.cut.id,
        draft: null,
      };
    case 'replaceCut':
      return withHistory(state, {
        ...state.project,
        cuts: state.project.cuts.map((c) => (c.id === action.cut.id ? action.cut : c)),
      });
    case 'removeCut':
      return {
        ...withHistory(state, {
          ...state.project,
          cuts: state.project.cuts.filter((c) => c.id !== action.id),
        }),
        selectedId: state.selectedId === action.id ? null : state.selectedId,
      };
    case 'clearCuts':
      return { ...withHistory(state, { ...state.project, cuts: [] }), selectedId: null, draft: null };
    case 'setFold': {
      const next: Project = {
        ...state.project,
        foldId: action.foldId,
        finishedSize: migrateSizeForFold(state.project, action.foldId),
        cuts: action.keepCuts ? state.project.cuts : [],
      };
      return { ...withHistory(state, next), selectedId: null, draft: null };
    }
    case 'setSize':
      return withHistory(state, {
        ...state.project,
        finishedSize: { ...state.project.finishedSize, value: action.value },
      });
    case 'setColor':
      return withHistory(state, { ...state.project, paperColor: action.color });
    case 'setGuides':
      return withHistory(state, {
        ...state.project,
        guides: { ...state.project.guides, ...action.patch },
      });
    case 'loadProject':
      return {
        ...state,
        project: action.project,
        past: [...state.past, state.project].slice(-MAX_HISTORY),
        future: [],
        selectedId: null,
        draft: null,
        notice: { kind: 'success', text: '作品已载入', at: Date.now() },
      };
    case 'loadPreset': {
      const cuts = buildPresetCuts(action.preset, state.project.finishedSize);
      return {
        ...withHistory(state, { ...state.project, foldId: action.preset.foldId, cuts }),
        selectedId: null,
        draft: null,
        notice: { kind: 'success', text: `已载入模板「${action.preset.name}」`, at: Date.now() },
      };
    }
    case 'undo': {
      if (!state.past.length) return state;
      const previous = state.past[state.past.length - 1];
      return {
        ...state,
        project: previous,
        past: state.past.slice(0, -1),
        future: [state.project, ...state.future].slice(0, MAX_HISTORY),
        selectedId: null,
        draft: null,
      };
    }
    case 'redo': {
      if (!state.future.length) return state;
      const next = state.future[0];
      return {
        ...state,
        project: next,
        past: [...state.past, state.project].slice(-MAX_HISTORY),
        future: state.future.slice(1),
        selectedId: null,
        draft: null,
      };
    }
    case 'notice':
      return { ...state, notice: action.notice };
    case 'saving':
      return { ...state, saving: action.saving };
    default:
      return state;
  }
}

export const STORAGE_KEY = 'a4-papercutting.project.v1';

export interface StorageStatus {
  available: boolean;
  message: string;
}

/** 读取本地自动保存，失败时给出可理解的原因而不是静默丢数据。 */
export function loadSaved(): { project: Project | null; status: StorageStatus } {
  try {
    if (typeof localStorage === 'undefined') {
      return { project: null, status: { available: false, message: '当前环境不支持本地保存' } };
    }
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { project: null, status: { available: true, message: '' } };
    const result = validateProject(JSON.parse(raw));
    if (!result.ok || !result.project) {
      return {
        project: null,
        status: { available: true, message: '本地保存的作品已损坏，已忽略并保留你的手动导出文件' },
      };
    }
    return { project: result.project, status: { available: true, message: '' } };
  } catch (err) {
    return {
      project: null,
      status: { available: true, message: `读取本地保存失败：${(err as Error).message}` },
    };
  }
}

export function useEditor(initialProject?: Project) {
  const saved = useMemo(() => loadSaved(), []);
  const startProject = initialProject ?? saved.project ?? createDefaultProject();

  const [state, dispatch] = useReducer(reducer, {
    project: startProject,
    tool: 'polygon',
    selectedId: null,
    draft: null,
    past: [],
    future: [],
    notice: saved.status.message
      ? { kind: 'warn', text: saved.status.message, at: Date.now() }
      : null,
    mode: 'folded',
    saving: false,
  });

  const saveTimer = useRef<number | null>(null);
  const projectRef = useRef(state.project);
  projectRef.current = state.project;

  /** 节流自动保存；配额或隐私模式失败时提示并保留手动导出入口。 */
  useEffect(() => {
    if (typeof localStorage === 'undefined') return;
    if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
    dispatch({ type: 'saving', saving: true });
    saveTimer.current = window.setTimeout(() => {
      try {
        localStorage.setItem(STORAGE_KEY, serializeProject(projectRef.current));
        dispatch({ type: 'saving', saving: false });
      } catch (err) {
        dispatch({ type: 'saving', saving: false });
        dispatch({
          type: 'notice',
          notice: {
            kind: 'error',
            text: `自动保存失败（${(err as Error).message}）。请立即使用「导出作品 JSON」手动保存。`,
            at: Date.now(),
          },
        });
      }
    }, 500);
    return () => {
      if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
    };
  }, [state.project]);

  const api = useMemo(
    () => ({
      dispatch,
      undo: () => dispatch({ type: 'undo' }),
      redo: () => dispatch({ type: 'redo' }),
    }),
    [],
  );

  const notify = useCallback(
    (kind: Notice['kind'], text: string) => {
      dispatch({ type: 'notice', notice: { kind, text, at: Date.now() } });
    },
    [dispatch],
  );

  return { state, ...api, notify, storage: saved.status };
}
